import React from 'react';
import { WifiOff } from 'lucide-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

/**
 * Persistent offline banner.
 *
 * Only renders when offline: a permanent "Online" chip would be noise on the
 * ~99% of taps where connectivity is fine, and it would compete with the fixed
 * header. Being offline is the exceptional, consequential state — photos queue
 * into localStorage and damage reports cannot be saved at all — so that is the
 * only state worth spending screen space and attention on.
 *
 * Amber, not red: offline is a degraded-but-working state (photos still
 * capture and queue), and DESIGN.md reserves red for genuinely destructive
 * things. role="status" so VoiceOver announces the transition.
 */
const OnlineStatusIndicator: React.FC = () => {
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Near-black on amber, not white: white on amber-500 is 2.15:1 and fails
      // AA outright. This is the one message that has to survive a bright
      // doorstep in direct sun, so it gets the highest-contrast pairing
      // available (7.62:1).
      className="sticky top-0 z-40 flex items-center justify-center gap-1.5 bg-amber-500 px-3 py-1 text-[#1b2027]"
      style={{ paddingTop: 'max(4px, env(safe-area-inset-top, 0px))' }}
    >
      <WifiOff size={14} aria-hidden="true" className="flex-shrink-0" />
      <span className="text-[12px] font-medium">
        Offline – foton sparas lokalt och laddas upp senare
      </span>
    </div>
  );
};

export default OnlineStatusIndicator;
